import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowsClockwise,
  Printer,
  DownloadSimple,
  MagnifyingGlass,
} from "@phosphor-icons/react";

export default function GlDetailPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const accountId = params.get("account") || "";
  const startParam = params.get("start") || "";
  const endParam = params.get("end") || "";

  const [detail, setDetail] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [moveLine, setMoveLine] = useState(null); // line row obj being reclassed
  const [moveTarget, setMoveTarget] = useState("");

  const load = async () => {
    if (!activeCompany || !accountId) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ account_id: accountId });
      if (startParam) q.append("start", startParam);
      if (endParam) q.append("end", endParam);
      const { data } = await api.get(
        `/companies/${activeCompany.id}/reports/gl-detail?${q.toString()}`,
      );
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeCompany) return;
    api.get(`/companies/${activeCompany.id}/accounts`).then((r) => setAccounts(r.data));
  }, [activeCompany?.id]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id, accountId, startParam, endParam]);

  const filtered = useMemo(() => {
    if (!detail?.lines) return [];
    const q = search.trim().toLowerCase();
    if (!q) return detail.lines;
    return detail.lines.filter(
      (l) =>
        (l.memo || "").toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q) ||
        (l.reference || "").toLowerCase().includes(q) ||
        (l.date || "").includes(q),
    );
  }, [detail, search]);

  const doMove = async () => {
    if (!moveLine || !moveTarget) return;
    if (moveTarget === moveLine.account_id) {
      return toast.error("Target must differ from source");
    }
    try {
      await api.post(
        `/companies/${activeCompany.id}/journals/${moveLine.journal_id}/lines/${moveLine.line_id}/move`,
        { to_account_id: moveTarget },
      );
      toast.success("Line reclassed");
      setMoveLine(null);
      setMoveTarget("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const exportCsv = () => {
    if (!detail?.lines) return;
    const rows = [
      ["Date", "Reference", "Memo", "Description", "Debit", "Credit", "Running balance"],
      ...detail.lines.map((l) => [
        l.date, l.reference || "", l.memo || "",
        l.description || "",
        l.debit || 0, l.credit || 0, l.running_balance,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(detail.account?.code || "GL")}_${detail.account?.name || accountId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const acc = detail?.account;

  return (
    <>
      <PageHeader
        eyebrow="Drilldown"
        title={acc ? `${acc.code} · ${acc.name}` : "GL Detail"}
        description={
          acc
            ? `Every journal line posted to this ${acc.type} account${
                startParam || endParam ? ` between ${startParam || "…"} and ${endParam || "…"}` : ""
              }.`
            : "Pick an account from a report."
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="gl-back-btn"
            >
              <ArrowLeft size={14} className="mr-1" />
              Back
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              data-testid="gl-print-btn"
            >
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              data-testid="gl-export-csv"
            >
              <DownloadSimple size={14} className="mr-1" /> CSV
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-4">
          <StatCell label="Opening" value={fmtMoney(detail?.opening_balance || 0, activeCompany.currency)} />
          <StatCell label="Debits" value={fmtMoney(detail?.total_debit || 0, activeCompany.currency)} />
          <StatCell label="Credits" value={fmtMoney(detail?.total_credit || 0, activeCompany.currency)} />
          <StatCell label="Closing" value={fmtMoney(detail?.closing_balance || 0, activeCompany.currency)} highlight />
        </div>

        <div className="mb-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search date / memo / reference…"
              className="pl-9 rounded-sm h-9"
              data-testid="gl-search"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            {filtered.length} of {detail?.lines?.length || 0} lines
          </div>
        </div>

        <div className="border border-border bg-card overflow-hidden">
          <table className="w-full text-sm" data-testid="gl-detail-table">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Date</Th>
                <Th>Reference</Th>
                <Th>Memo / Description</Th>
                <Th className="text-right">Debit</Th>
                <Th className="text-right">Credit</Th>
                <Th className="text-right">Running balance</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-muted-foreground">
                    No postings in this period.
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.line_id || l.journal_id} data-testid={`gl-line-${l.line_id}`}
                    className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
                  <Td className="num">{l.date}</Td>
                  <Td className="num">{l.reference || "—"}</Td>
                  <Td>
                    <div className="font-medium">{l.memo || "—"}</div>
                    {l.description && (
                      <div className="text-xs text-muted-foreground">{l.description}</div>
                    )}
                  </Td>
                  <Td className="text-right num">{l.debit ? l.debit.toFixed(2) : "—"}</Td>
                  <Td className="text-right num">{l.credit ? l.credit.toFixed(2) : "—"}</Td>
                  <Td className="text-right num font-medium">{l.running_balance.toFixed(2)}</Td>
                  <Td className="text-right pr-4">
                    <div className="inline-flex gap-1">
                      <Link
                        to={`/journals?open=${l.journal_id}`}
                        className="text-xs text-primary hover:underline"
                        data-testid={`gl-jump-${l.line_id}`}
                      >
                        View →
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() =>
                          setMoveLine({ ...l, account_id: accountId })
                        }
                        data-testid={`gl-move-${l.line_id}`}
                        title="Move this line to a different GL"
                      >
                        <ArrowsClockwise size={12} />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>

      <Dialog open={!!moveLine} onOpenChange={(v) => !v && setMoveLine(null)}>
        <DialogContent className="rounded-sm" data-testid="gl-move-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Move line to another GL</DialogTitle>
          </DialogHeader>
          {moveLine && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-sm border bg-muted/40">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Line</div>
                <div className="font-medium mt-1">{moveLine.memo || moveLine.description || "—"}</div>
                <div className="text-xs mt-1 num">
                  {moveLine.date} · Dr {moveLine.debit?.toFixed(2)} · Cr {moveLine.credit?.toFixed(2)}
                </div>
                <div className="text-xs mt-1 text-muted-foreground">
                  Currently on <b>{acc?.code} · {acc?.name}</b>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Move to</Label>
                <Select value={moveTarget} onValueChange={setMoveTarget}>
                  <SelectTrigger className="rounded-sm h-9 font-mono" data-testid="gl-move-target">
                    <SelectValue placeholder="Pick a GL account" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {accounts
                      .filter((a) => a.id !== accountId)
                      .map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.code} · {a.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-muted-foreground">
                Only this single line moves. The balancing line stays where it is, so the journal
                remains in balance.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMoveLine(null)}>Cancel</Button>
            <Button onClick={doMove} disabled={!moveTarget} data-testid="gl-move-confirm">
              Move line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCell({ label, value, highlight }) {
  return (
    <div className={`bg-card p-4 ${highlight ? "bg-primary/5" : ""}`}>
      <div className="overline">{label}</div>
      <div className={`font-serif-display text-2xl num mt-1 ${highlight ? "text-primary" : ""}`}>
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "", onClick }) => (
  <td onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</td>
);
